import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import each from 'lodash/each';
import keys from 'lodash/keys';
import isNull from 'lodash/isNull';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { Button, makeStyles } from '@material-ui/core';
import { getI18n } from "../../../utils/intl";

import {
  API_PER_ORG_RANK_FILTER_TYPE_OVER_ALL_RANK,
  API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_API,
  API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_ORG,
  FILTER_TYPE_RANK,
  FILTER_TYPE_RANK_API_PER_ORG,
  FILTER_TYPE_NAME,
  FILTER_TYPE_NAME_API_PER_ORG,
  FILTER_ENTITY_TYPE_API_PER_ORG,
} from '../../../constants/filter';
import { getSearchPlaceHolderText } from '../utils/filter';

import FilterByRank from '../FilterByRank';
import FilterByRankAPIPerOrg from '../FilterByRankAPIPerOrg';
import FilterByName from '../FilterByName';
import FilterByNameAPIPerOrg from '../FilterByNameAPIPerOrg';

import styles from './styles';

const UsageFilter = (props) => {
  const {
    entityNameMap, filterInitState, title, handleFilterChange, closeFilter, showFilter,
    maxAllowedEntities, isDeveloper, isOrgAdmin, isOrgPublisher, userOrgUuid, entityType,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    selectedItems: {},
    selectedRank: 1,
    selectedRankType: API_PER_ORG_RANK_FILTER_TYPE_OVER_ALL_RANK,
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  useEffect(() => {
    const { filterType, selectedItems, rank, rankType,
      isDeveloper, isOrgAdmin, isOrgPublisher, userOrgUuid } = filterInitState;

    // Duplicating the object as changes to
    // state.selectedItems affecting props.filterInitState.selectedItems
    const selectedItemsIds = keys(selectedItems);
    let selectedItemIdsMap = {};
    let selectedRank = rank;
    each(selectedItemsIds, item => { selectedItemIdsMap[item] = true; });
    if ((state.filterType === FILTER_TYPE_RANK_API_PER_ORG) &&
      (isDeveloper || isOrgAdmin || isOrgPublisher)) {
      if (rankType === API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_ORG) {
        const entities = keys(entityNameMap);
        const orgEntities = entities.map(item => item.split('##')[1]);
        if (orgEntities.includes(userOrgUuid)) {
          selectedItemIdsMap = {
            [userOrgUuid]: true,
          };
        } else {
          selectedItemIdsMap = {};
        }
      } else if (rankType === API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_API) {
        selectedRank = 1;
      }
    }
    setState({
      filterType,
      selectedItems: selectedItemIdsMap || {},
      selectedRank: selectedRank || 3,
      selectedRankType: rankType || API_PER_ORG_RANK_FILTER_TYPE_OVER_ALL_RANK,
    });
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClick, false);
    document.addEventListener('keydown', handleKeyDown, false);

    return () => {
      document.removeEventListener('mousedown', handleClick, false);
      document.removeEventListener('keydown', handleKeyDown, false);
    }
  }, []);

  const getApplyBtnStatus = () => {
    const { filterType, selectedRank, selectedItems,
      isInvalidRankSize, selectedRankType } = state;
    let selectedEntities = 0;
    if (filterType === FILTER_TYPE_RANK) {
      if (!isInvalidRankSize) {
        selectedEntities = selectedRank;
      }
    } else if (filterType === FILTER_TYPE_NAME) {
      selectedEntities = keys(selectedItems).length;
    } else if (filterType === FILTER_TYPE_RANK_API_PER_ORG) {
      if (!isInvalidRankSize) {
        selectedEntities = selectedRank;
      }
      if (selectedRankType !== API_PER_ORG_RANK_FILTER_TYPE_OVER_ALL_RANK) {
        selectedEntities *= keys(selectedItems).length;
      }
    } else if (filterType === FILTER_TYPE_NAME_API_PER_ORG) {
      selectedEntities = keys(selectedItems).length;
    }
    return ((selectedEntities > 0) && (selectedEntities <= maxAllowedEntities));
  }

  const getTabLabel = (filterType) => {
    const msgIdsMap = {
      FILTER_TYPE_RANK: 'label.analytics.filter.type.filterByRank.label',
      FILTER_TYPE_NAME: 'label.analytics.filter.type.filterByName.label',
      FILTER_TYPE_RANK_API_PER_ORG: 'label.analytics.filter.type.filterByRank.label',
      FILTER_TYPE_NAME_API_PER_ORG: 'label.analytics.filter.type.filterByName.label',
    };
    return intl.getI18nMessage(msgIdsMap[filterType]);
  };

  const getTabs = (entityType) => {
    if (entityType === FILTER_ENTITY_TYPE_API_PER_ORG) {
      return [{
        type: FILTER_TYPE_RANK_API_PER_ORG,
        name: intl.getI18nMessage('label.analytics.filter.type.filterByRank.label'),
      }, {
        type: FILTER_TYPE_NAME_API_PER_ORG,
        name: intl.getI18nMessage('label.analytics.filter.type.filterByName.label'),
      }];
    }
    return [{
      type: FILTER_TYPE_RANK,
      name: intl.getI18nMessage('label.analytics.filter.type.filterByRank.label'),
    }, {
      type: FILTER_TYPE_NAME,
      name: intl.getI18nMessage('label.analytics.filter.type.filterByName.label'),
    }];
  };

  const handleClick = (event) => {
    if (event.which === 1) {
      const targetElement = event.target;
      const filterContainer = targetElement.closest('#filter-container');
      if (isNull(filterContainer)) {
        closeFilter();
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.keyCode === 27) {
      closeFilter();
    }
  }

  const toggleFilterType = filterType => {
    if (filterType !== state.filterType) {
      // Duplicating the object as changes to
      // state.selectedItems affecting props.filterInitState.selectedItems
      const { selectedItems, rank, rankType } = filterInitState;
      const selectedItemsIds = keys(selectedItems);
      const selectedItemIdsMap = {};
      each(selectedItemsIds, item => { selectedItemIdsMap[item] = true; });

      setState({
        filterType,
        selectedItems: selectedItemIdsMap,
        selectedRank: rank || 3,
        selectedRankType: rankType ||
          API_PER_ORG_RANK_FILTER_TYPE_OVER_ALL_RANK,
        isInvalidRankSize: false,
      });
    }
  };

  const updateRankSize = (selectedRank, isInvalid) => {
    setState({ selectedRank, isInvalidRankSize: isInvalid });
  }

  const updateRankTypeSizeAndSelectedItmes =
    (selectedRank, selectedRankType, isInvalid, selectedItems) => {
      let rankSize = selectedRank;
      let selectedItemIdsMap = selectedItems;
      if ((state.filterType === FILTER_TYPE_RANK_API_PER_ORG) &&
        (isDeveloper || isOrgAdmin || isOrgPublisher)) {
        if (selectedRankType === API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_ORG) {
          const entities = keys(entityNameMap);
          const orgEntities = entities.map(item => item.split('##')[1]);
          if (orgEntities.includes(userOrgUuid)) {
            selectedItemIdsMap = {
              [userOrgUuid]: true,
            };
          } else {
            selectedItemIdsMap = {};
          }
        } else if (selectedRankType === API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_API) {
          rankSize = 1;
        }
      }
      setState({
        selectedRankType,
        selectedRank: rankSize,
        isInvalidRankSize: isInvalid,
        selectedItems: selectedItemIdsMap,
      });
    };

  const updateSelectedItems = selectedItems => {
    setState({ selectedItems });
  }

  const applyFilter = () => {
    const { filterType, selectedRank, selectedRankType, selectedItems } = state;
    const filterData = {
      entityType: entityType,
      filterType,
    };
    if (filterType === FILTER_TYPE_RANK) {
      filterData.rank = selectedRank;
    } else if (filterType === FILTER_TYPE_RANK_API_PER_ORG) {
      filterData.rank = selectedRank;
      filterData.rankType = selectedRankType;
      filterData.selectedItems = selectedItems;
    } else if (filterType === FILTER_TYPE_NAME) {
      filterData.selectedItems = selectedItems;
    } else if (filterType === FILTER_TYPE_NAME_API_PER_ORG) {
      filterData.selectedItems = selectedItems;
    }
    handleFilterChange(filterData);
  };

  const renderFilter = (filterType) => {
    const { selectedRank, selectedRankType, selectedItems } = state;
    const placeHolderText = getSearchPlaceHolderText(entityType, intl);

    if (filterType === FILTER_TYPE_NAME) {
      return (<FilterByName
        filterData={entityNameMap}
        placeHolderText={placeHolderText}
        selectedItems={selectedItems}
        updateSelectedItems={updateSelectedItems}
        maxAllowedEntities={maxAllowedEntities}
      />);
    } else if (filterType === FILTER_TYPE_RANK) {
      return (<FilterByRank
        updateRankSize={updateRankSize}
        updatedFilterRankSize={selectedRank}
        maxAllowedEntities={maxAllowedEntities}
      />);
    } else if (filterType === FILTER_TYPE_RANK_API_PER_ORG) {
      let disableRankSelection = false;
      let disableItemSelection = false;
      if (isDeveloper || isOrgAdmin || isOrgPublisher) {
        if (selectedRankType === API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_API) {
          disableRankSelection = true;
        } else if (selectedRankType === API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_ORG) {
          disableItemSelection = true;
        }
      }
      return (<FilterByRankAPIPerOrg
        isInvalidRankSize={state.isInvalidRankSize}
        updateRankTypeSizeAndSelectedItmes={updateRankTypeSizeAndSelectedItmes}
        updatedFilterRankSize={selectedRank}
        updatedFilterRankType={selectedRankType}
        maxAllowedEntities={maxAllowedEntities}
        filterData={entityNameMap}
        selectedItems={selectedItems}
        disableRankSelection={disableRankSelection}
        disableItemSelection={disableItemSelection}
      />);
    } else if (filterType === FILTER_TYPE_NAME_API_PER_ORG) {
      return (<FilterByNameAPIPerOrg
        filterData={entityNameMap}
        maxAllowedEntities={maxAllowedEntities}
        placeHolderText={placeHolderText}
        selectedItems={selectedItems}
        showSecondaryText
        updateSelectedItems={updateSelectedItems}
      />);
    }
    return (<div />);
  };

  const renderFilterTabs = () => {
    const tabs = getTabs(entityType);
    return (<div className={classes.filterTypeContainer}>
      {tabs.map(item => (
        <span key={item.type}>
          <Button
            onClick={() => toggleFilterType(item.type)}
            className={clsx(
              classes.filterTypeBtn,
              state.filterType === item.type
                ? classes.filterTypeBtnSelected
                : null,
            )}
          >
            {getTabLabel(item.type)}
          </Button>
        </span>
      ))}
    </div>);
  };

  const disableApplyBtn = !(getApplyBtnStatus());
  return showFilter ? (
    <div
      className={classes.filter}
      id={'filter-container'}
    >
      <div className={classes.filterTitle}>{title}</div>
      {renderFilterTabs()}
      {renderFilter(state.filterType)}
      <div className={classes.buttonBar}>
        <Button
          onClick={applyFilter}
          className={clsx(
            classes.applyBtn,
            disableApplyBtn ? classes.disableBtn : null,
          )}
        >
          {intl.getI18nMessage('label.analytics.dashboard.label.apply')}
        </Button>
        <Button
          className={classes.cancelBtn}
          primary
          onClick={closeFilter}
        >
          {intl.getI18nMessage('label.analytics.dashboard.label.cancel')}
        </Button>
      </div>
    </div>
  ) : null;
}

UsageFilter.propTypes = {
  closeFilter: PropTypes.func,
  entityNameMap: PropTypes.object,
  entityType: PropTypes.string,
  filterInitState: PropTypes.object,
  handleFilterChange: PropTypes.func,
  showFilter: PropTypes.bool,
  title: PropTypes.string,
  maxAllowedEntities: PropTypes.number,
  isOrgAdmin: PropTypes.bool,
  isOrgPublisher: PropTypes.bool,
  isDeveloper: PropTypes.bool,
  userOrgUuid: PropTypes.string,
};

export default UsageFilter;
